import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Calendar, 
  Clock, 
  User, 
  Phone, 
  Mail, 
  Package, 
  CheckCircle2, 
  XCircle, 
  Archive,
  MessageCircle,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  confirmed: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200'
};

const statusLabels = {
  pending: 'In Attesa',
  confirmed: 'Confermata',
  completed: 'Completata',
  cancelled: 'Annullata'
};

export const AdminDashboard = () => {
  const { toast } = useToast();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    completed: 0
  });

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
      
      // Calculate stats
      const newStats = {
        total: response.data.length,
        pending: response.data.filter(b => b.status === 'pending').length,
        confirmed: response.data.filter(b => b.status === 'confirmed').length,
        completed: response.data.filter(b => b.status === 'completed').length
      };
      setStats(newStats);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare le prenotazioni",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}`, { status: newStatus });
      toast({
        title: "Aggiornato!",
        description: `Prenotazione ${statusLabels[newStatus].toLowerCase()}`,
      });
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
      toast({
        title: "Errore",
        description: "Impossibile aggiornare la prenotazione",
        variant: "destructive"
      });
    }
  };

  const openWhatsApp = (phone, customerName, product) => {
    const message = encodeURIComponent(
      `Ciao ${customerName}! Ti confermiamo la disponibilità di ${product}. Quando passi a ritirarlo?`
    );
    window.open(`https://wa.me/${phone.replace(/\s+/g, '')}?text=${message}`, '_blank');
  };

  const filteredBookings = activeTab === 'all' 
    ? bookings 
    : bookings.filter(b => b.status === activeTab);

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Dashboard Prenotazioni</h1>
          <p className="admin-subtitle">Gestisci le prenotazioni prodotti SVAPO</p>
        </div>
        <Button onClick={fetchBookings} variant="outline" className="admin-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Aggiorna
        </Button>
      </div>

      <div className="admin-stats">
        <Card className="stat-card stat-total">
          <div className="stat-icon">
            <Package className="w-6 h-6" />
          </div>
          <div>
            <p className="stat-label">Totale</p>
            <p className="stat-value">{stats.total}</p>
          </div>
        </Card>

        <Card className="stat-card stat-pending">
          <div className="stat-icon">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <p className="stat-label">In Attesa</p>
            <p className="stat-value">{stats.pending}</p>
          </div>
        </Card>

        <Card className="stat-card stat-confirmed">
          <div className="stat-icon">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <div>
            <p className="stat-label">Confermate</p>
            <p className="stat-value">{stats.confirmed}</p>
          </div>
        </Card>

        <Card className="stat-card stat-completed">
          <div className="stat-icon">
            <Archive className="w-6 h-6" />
          </div>
          <div>
            <p className="stat-label">Completate</p>
            <p className="stat-value">{stats.completed}</p>
          </div>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="admin-tabs">
        <TabsList className="admin-tabs-list">
          <TabsTrigger value="all">Tutte ({stats.total})</TabsTrigger>
          <TabsTrigger value="pending">In Attesa ({stats.pending})</TabsTrigger>
          <TabsTrigger value="confirmed">Confermate ({stats.confirmed})</TabsTrigger>
          <TabsTrigger value="completed">Completate ({stats.completed})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="admin-tabs-content">
          {loading ? (
            <div className="admin-loading">Caricamento...</div>
          ) : filteredBookings.length === 0 ? (
            <Card className="admin-empty">
              <Package className="admin-empty-icon" />
              <p className="admin-empty-text">Nessuna prenotazione trovata</p>
            </Card>
          ) : (
            <div className="bookings-grid">
              {filteredBookings.map((booking) => (
                <Card key={booking.id} className="booking-card">
                  <div className="booking-card-header">
                    <Badge className={`booking-status ${statusColors[booking.status]}`}>
                      {statusLabels[booking.status]}
                    </Badge>
                    <span className="booking-date">
                      {format(new Date(booking.createdAt), 'dd MMM yyyy', { locale: it })}
                    </span>
                  </div>

                  <div className="booking-card-body">
                    <div className="booking-info-row">
                      <User className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="booking-label">Cliente</p>
                        <p className="booking-value">{booking.customerName}</p>
                      </div>
                    </div>

                    <div className="booking-info-row">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="booking-label">Telefono</p>
                        <p className="booking-value">{booking.customerPhone}</p>
                      </div>
                    </div>

                    <div className="booking-info-row">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="booking-label">Email</p>
                        <p className="booking-value">{booking.customerEmail}</p>
                      </div>
                    </div>

                    <div className="booking-divider"></div>

                    <div className="booking-product">
                      <Package className="w-5 h-5 text-cyan-600" />
                      <div>
                        <p className="booking-product-brand">{booking.productBrand}</p>
                        <p className="booking-product-model">{booking.productModel}</p>
                      </div>
                    </div>

                    {booking.pickupDate && (
                      <div className="booking-pickup">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span>
                          {format(new Date(booking.pickupDate), 'dd MMM yyyy', { locale: it })}
                          {booking.pickupTime && ` alle ${booking.pickupTime}`}
                        </span>
                      </div>
                    )}

                    {booking.notes && (
                      <div className="booking-notes">
                        <p className="booking-notes-label">Note:</p>
                        <p className="booking-notes-text">{booking.notes}</p>
                      </div>
                    )}
                  </div>

                  <div className="booking-card-actions">
                    {booking.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                          className="booking-btn-confirm"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Conferma
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                          className="booking-btn-cancel"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Rifiuta
                        </Button>
                      </>
                    )}

                    {booking.status === 'confirmed' && (
                      <Button
                        size="sm"
                        onClick={() => updateBookingStatus(booking.id, 'completed')}
                        className="booking-btn-complete"
                      >
                        <Archive className="w-4 h-4 mr-1" />
                        Completa
                      </Button>
                    )}

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openWhatsApp(
                        booking.customerPhone,
                        booking.customerName,
                        `${booking.productBrand} ${booking.productModel}`
                      )}
                      className="booking-btn-whatsapp"
                    >
                      <MessageCircle className="w-4 h-4 mr-1" />
                      WhatsApp
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
